import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { navigate } from 'gatsby';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import TextInput from '@ui/forms/TextInput';

import SubmitButton from '@ui/molecules/SubmitButton';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { useTracking } from '@hooks/useTracking';

import { isCapsuleEligible } from '@helpers/Delivery';

import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';

import {
	IAddressInfo,
	IAddressInfoMutationData,
	IAddressInfoMutationResponse,
} from '@folx-types/address';
import { IMemberData } from '@folx-types/member';

import ADDRESS_INFO_MUTATION from '@graphql/mutations/AddressInfo.mutation.gql';

import theme from '@theme/FolxTheme';

import { schema } from './ShippingEditStep.data';
import { IShippingEditPageContent } from './ShippingEditStep.page';

interface IShippingEditStepProps {
	address?: IAddressInfo;
	member?: IMemberData;
	deliveryMethod?: string;
	refinedContent: IShippingEditPageContent;
}

const FullWidthColumn = fullWidth(FlexColumn);
const FullWidthSubmitButton = fullWidth(SubmitButton);

const PageHeading = styled(HeadingText)`
	margin-bottom: ${theme.spacing.md};
`;

const ShippingEditStep: FC<IShippingEditStepProps> = ({
	address,
	member,
	deliveryMethod,
	refinedContent,
}) => {
	const { trackEvent } = useTracking('member-portal-view');

	const [submitting, setSubmitting] = useState<boolean>(false);
	const [hasCapsuleError, setHasCapsuleError] = useState<boolean>(false);

	const [updateAddressInfo, { loading: updateAddressLoading, error: updateAddressError }] =
		useMutation<IAddressInfoMutationResponse>(ADDRESS_INFO_MUTATION);

	type Schema = Partial<z.input<typeof schema>>;

	const {
		butterPage: {
			title,
			info,
			addressLine1Label,
			addressLine2Label,
			cityLabel,
			stateLabel,
			stateFineprint,
			zipLabel,
			genericError,
			capsuleError,
		},
	} = refinedContent;

	const form = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			...address,
		},
	});

	const {
		control,
		watch,
		register,
		setValue,
		handleSubmit,
		setFocus,
		reset,
		resetField,
		formState: { errors, isSubmitted },
	} = form;

	console.log(deliveryMethod);
	const checkZipCapsuleEligible = async (zip: string) => {
		const authorizationToken = await getAuthToken();

		const response = await axios.get(
			`${ApiEndpoints.public}/available-rx-delivery-methods/${zip}`,
			{
				headers: {
					authorizationToken,
				},
				responseType: 'json',
			}
		);

		const data = response?.data;
		return isCapsuleEligible(data);
	};

	const updateMemberAddressInfo = async (input: IAddressInfoMutationData) => {
		try {
			await updateAddressInfo({
				variables: input,
			});
			trackEvent('member-portal-update-address-info');
		} catch (e) {
			setSubmitting(false);
			console.error(`Oof. We've run into a problem™ updating your information. ${e}`);
		}
	};

	const saveAddressInfo = async (formData: IAddressInfoMutationData['patch']) => {
		setSubmitting(true);

		if (!member?.id) {
			return;
		}

		if (formData.zip && deliveryMethod == medicationDeliveryConstants.CAPSULE_METHOD) {
			const zipEligible = await checkZipCapsuleEligible(formData.zip);
			console.log(zipEligible);
			if (!zipEligible) {
				setSubmitting(false);
				setHasCapsuleError(true);
				return;
			}
		}

		await updateMemberAddressInfo({
			clientMutationId: member?.id,
			memberId: member?.id,
			patch: formData,
		});

		setSubmitting(false);
		navigate('..');
	};

	return (
		<FullWidthColumn>
			<form id="shipping" onSubmit={handleSubmit(saveAddressInfo)}>
				<PageHeading>{title}</PageHeading>
				<FlexColumn gap="lg">
					<BasicText>{info}</BasicText>
					<TextInput
						label={addressLine1Label}
						placeholder="123 Apple Rd"
						error={!!errors.line1}
						errorLabel={errors.line1?.message || ''}
						{...register('line1')}
					/>
					<TextInput
						label={addressLine2Label}
						placeholder="Apt 3 or Suite 43"
						error={!!errors.line2}
						errorLabel={errors.line2?.message || ''}
						{...register('line2')}
					/>
					<TextInput
						label={cityLabel}
						placeholder="Boston"
						error={!!errors.city}
						errorLabel={errors.city?.message || ''}
						{...register('city')}
					/>
					<FullWidthColumn>
						<FlexColumn gap="sm">
							<BasicText variant="field">{stateLabel}</BasicText>
							<BasicText>{address?.state}</BasicText>
							<BasicText variant="fineprint">{stateFineprint}</BasicText>
						</FlexColumn>
					</FullWidthColumn>
					<TextInput
						label={zipLabel}
						placeholder="20004"
						error={!!errors.zip}
						errorLabel={errors.zip?.message || ''}
						{...register('zip')}
					/>
					<FullWidthSubmitButton
						label="Save"
						type="submit"
						variant="secondary"
						submitting={submitting}
					/>
					<CenteredLink variant="underline" to="..">
						Cancel
					</CenteredLink>
					{exists(updateAddressError) && (
						<WYSIWYGContent
							color="red"
							tint="darken40"
							linkColor="black"
							dangerouslySetInnerHTML={{ __html: genericError }}
						/>
					)}
					{hasCapsuleError && (
						<WYSIWYGContent
							color="red"
							tint="darken40"
							linkUnderline
							dangerouslySetInnerHTML={{ __html: capsuleError }}
						/>
					)}
				</FlexColumn>
			</form>
		</FullWidthColumn>
	);
};

export default ShippingEditStep;
